import {
  Box,
  Typography,
  Button,
  AppBar,
  CardMedia,
  Divider,
  IconButton,
  Paper,
  Tooltip,
} from '@mui/material';
import { useParams } from 'react-router-dom';
import { useEffect, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import {
  Applicant,
  ApplicationProps,
  Document,
  esignApplicationResponse,
} from '../../redux-store/types/api-types';
import {
  esignApplication,
  getApplicationDetailsWithRefId,
} from '../../redux-store/actions/onBoarding';
import { useHistory, useLocation } from 'react-router';
import { RootStateType } from '../../redux-store/reducers';
import { Footer } from '../commonComponents';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import { ESIGN_MESSAGE, ESIGN_STATUS, KYC_VALIDATION_START_DATE } from '../../utils/constant';
import { getDocuments, isFormValidForSubmission } from '../../redux-store/actions/application';
import { getUserInfo, logout, showError } from '../../redux-store/actions/auth';
import { ApplicationDetailsCommonLayout } from '../ApplicationDetails/index';
import { getNationalityList } from '../../redux-store/actions';
import { nationaliyType } from '../../redux-store/types/mdms';
import { usePaymentFlow } from '../payment/hooks/usePaymentFlow';
import { PaymentPopups } from '../payment/PaymentPopups';
import {
  isIndividualResidentApplicant,
  IsOutBoundFundFlow,
  decodeState,
  isCKYCMethod,
  isUSTechFund,
  checkValidationBasedOnDate,
} from '../../utils/utilityFunctions';
import { useLeegalityEsign } from '../../utils/useLeegalityEsign';
import { useDigilockerFetch } from '../../digilocker/hooks/useDigilockerFetch';
import { LoadingButton } from '@mui/lab';
import MFCheckbox from '../../lib/formik/Checkbox';
import { Formik } from 'formik';
import { DecodedState } from '../../digilocker/loader/DigiLoader';
import {
  esignKycDocument,
  getInvestorTokenFromLanding,
  updateDigilockerAddress,
} from '../../digilocker/redux/actions/digilocker';
import { useConfirmDialog } from '../../digilocker/popup/useConfirmDialog';
import { ExitToApp } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';

export const Item = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  paddingTop: '10px',
  marginLeft: '2px',
  color: theme.palette.text.secondary,
  fontSize: '15px',
  fontWeight: 500,
  letterSpacing: '0.5px',
}));
export const ItemDetails = styled('div')(({ theme }) => ({
  ...theme.typography.body2,
  color: theme.palette.text.secondary,
  fontSize: '16px',
  marginLeft: '2px',
  fontWeight: 700,
  letterSpacing: '0.5px',
  maxWidth: '100%',
  overflowWrap: 'anywhere',
}));
export const Gridstyles = styled('div')(({ theme }) => ({
  paddingLeft: '10%',
  [theme.breakpoints.only('xs')]: {
    paddingLeft: 0,
  },
}));

export default function InvestorApplication(): JSX.Element {
  const { referenceId } = useParams<{ referenceId: string }>();
  const dispatch = useDispatch();
  const history = useHistory();
  const { investor, auth } = useSelector((store: RootStateType) => store);
  const [loading, setLoading] = useState(false);
  const [application, setApplication] = useState<ApplicationProps>();
  const applicationDetailRef = useRef<HTMLDivElement>();
  const digilockerUpdateStartedRef = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { openSigningPopup, initSigningPopup, startEsign, cancelSigning, isSigningOpen } =
    useLeegalityEsign();
  const { enqueueSnackbar } = useSnackbar();
  const [digiLockerConsent, setDigiLockerConsent] = useState(false);

  // Refetch the application from the backend (used after successful signing)
  const refetchApplication = async () => {
    try {
      const _application = (await dispatch(
        getApplicationDetailsWithRefId(referenceId)
      )) as unknown as ApplicationProps;
      setApplication(_application);
    } catch (err) {
      console.error('Failed to refetch application:', err);
    }
  };

  const { triggerDigilocker, loading: digiLockerLoading } = useDigilockerFetch({
    applicationId: application?.id || '',
    token: investor.token,
    role: 'investor',
    referenceId,
    origin: window.location.origin,
    digiLockerConsent: digiLockerConsent,
  });

  const { confirm, ConfirmDialog } = useConfirmDialog();

  const handleKycFetched = async (digilockerId: string) => {
    const shouldUpdate = await confirm({
      title: 'KYC Fetched Successfully!',
      description:
        'Would you like to update the application form with your KYC details from DigiLocker?',
      confirmLabel: 'Update Details',
      cancelLabel: "Don't Update Details",
    });
    if (shouldUpdate) {
      await dispatch(updateDigilockerAddress(digilockerId, true));
      const _application = (await dispatch(
        getApplicationDetailsWithRefId(referenceId)
      )) as unknown as ApplicationProps;
      setApplication(_application);
    } else {
      await dispatch(updateDigilockerAddress(digilockerId, false));
    }
  };

  const location = useLocation();
  const query = new URLSearchParams(location.search);
  const state = query.get('state');

  useEffect(() => {
    (async function () {
      if (state && application?.id && !digilockerUpdateStartedRef.current) {
        digilockerUpdateStartedRef.current = true;
        const { token = '', digilockerId = '' } = decodeState<DecodedState>(state) || {};
        await dispatch(getInvestorTokenFromLanding({ token }));
        query.delete('state');
        query.delete('code');
        history.replace({
          pathname: location.pathname,
          search: query.toString(),
        });
        // await handleKycFetched(digilockerId);
      }
    })();
  }, [application?.id, state]);

  const {
    paymentStepComplete,
    isOverseas,
    isPaymentSubmitting,
    isOnlinePaymentEnabled,
    showPaymentSuccessPopup,
    setShowPaymentSuccessPopup,
    showPaymentFailurePopup,
    setShowPaymentFailurePopup,
    showPaymentModePopup,
    setShowPaymentModePopup,
    showOfflinePaymentEsignPopup,
    setShowOfflinePaymentEsignPopup,
    handleMakePayment,
    handlePaymentModeConfirm,
    paymentCompleted,
    markPaymentOfflineAndProceed,
  } = usePaymentFlow({ application });

  useEffect(() => {
    (async function () {
      try {
        if (state) {
          const { token = '' } = decodeState<DecodedState>(state) || {};
          if (token) {
            await dispatch(getInvestorTokenFromLanding({ token }));
          }
        }
        await dispatch(getUserInfo());
        const _application = (await dispatch(
          getApplicationDetailsWithRefId(referenceId)
        )) as unknown as ApplicationProps;
        setApplication(_application);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    })();
    setLoading(true);
  }, []);

  useEffect(() => {
    let redirectedStateToken = null;
    const { token } = investor;
    if (state) {
      const { token = '' } = decodeState<DecodedState>(state) || {};
      redirectedStateToken = token;
    }
    if (!(token || redirectedStateToken)) {
      history.push(`/investment-details/${referenceId}/details`);
    }
  }, []);

  useEffect(() => {
    document.title = 'Edelweiss Gift City :: Application Details';
  }, []);

  const handleKycEsign = async (referenceId: string, onSuccess: () => void) => {
    const kycEsignCallbacks = {
      onSuccess: () => {
        enqueueSnackbar('KYC document signed successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
        onSuccess();
      },
      onError: (error: string) => {
        enqueueSnackbar(error || 'Signing was not completed. Please try again.', {
          variant: 'error',
          autoHideDuration: 4000,
        });
      },
    };
    // Open the popup FIRST (synchronous) so the browser doesn't block it,
    // then fetch the sign URL and navigate the popup to it.
    const popupOpened = initSigningPopup(kycEsignCallbacks);
    try {
      setIsSubmitting(true);
      const response = (await dispatch(esignKycDocument(referenceId))) as unknown as any;

      if (popupOpened) {
        startEsign(response?.url);
      } else {
        openSigningPopup(response?.url, kycEsignCallbacks);
      }
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
      console.error((e as Error).message);
      // Close the popup if we opened it but hit an error
      cancelSigning();
    } finally {
      setIsSubmitting(false);
    }
  };

  const esignCallbacks = {
    onSuccess: async () => {
      enqueueSnackbar('Document signed successfully', {
        variant: 'success',
        autoHideDuration: 3000,
      });
      await refetchApplication();
    },
    onError: (error: string) => {
      enqueueSnackbar(error || 'Signing was not completed. Please try again.', {
        variant: 'error',
        autoHideDuration: 4000,
      });
    },
  };

  const handleEsign = async () => {
    // Open the popup FIRST (synchronous) so the browser doesn't block it.
    // The URL is navigated to only after validation/API calls complete.
    const popupOpened = initSigningPopup(esignCallbacks);
    try {
      setIsSubmitting(true);
      const nationalitiesMdmsMasters = (await dispatch(
        getNationalityList()
      )) as unknown as nationaliyType;
      const documentsResponse = (await dispatch(
        getDocuments(application?.flowType || '', application?.fundFlowType || '')
      )) as unknown as Document;
      if (ESIGN_STATUS.PENDING_SIGNATURE !== application?.signDetails.status) {
        ESIGN_STATUS.NOT_GENERATED === application?.signDetails.status
          ? await isFormValidForSubmission(
              application as ApplicationProps,
              true,
              true,
              nationalitiesMdmsMasters,
              documentsResponse,
              auth.role
            )
          : await isFormValidForSubmission(
              application as ApplicationProps,
              true,
              false,
              nationalitiesMdmsMasters,
              documentsResponse,
              auth.role
            );
      }
      const { signDetails, flowType = '' } = application || {};
      if (signDetails?.url) {
        if (popupOpened) {
          startEsign(signDetails.url);
        } else {
          openSigningPopup(signDetails.url, esignCallbacks);
        }
        return;
      }
      const response = (await dispatch(
        esignApplication(referenceId, flowType)
      )) as unknown as esignApplicationResponse;
      setApplication({ ...application, signDetails: response } as unknown as ApplicationProps);
      if (popupOpened) {
        startEsign(response?.url);
      } else {
        openSigningPopup(response?.url, esignCallbacks);
      }
    } catch (e) {
      typeof e === 'string' && dispatch(showError(e as string));
      console.error((e as Error).message);
      // Close the popup if we opened it but hit an error/validation failure
      cancelSigning();
    } finally {
      setIsSubmitting(false);
    }
  };

  const effectivePaymentStepComplete = isIndividualResidentApplicant(
    application?.applicants?.[0]?.status
  )
    ? paymentStepComplete
    : true;

  const isOutbound = IsOutBoundFundFlow(application?.fundFlowType || '');
  const isUSTech = isUSTechFund(application?.schemeId?.toString() || '');
  const isCKYC = isCKYCMethod(application?.scheme?.journeyType || '');

  const isDateEligibleForKycPayment = checkValidationBasedOnDate(
    application?.createdAt as string,
    KYC_VALIDATION_START_DATE
  );

  // Get correct Applicant
  const applicant = application?.applicants?.find(
    (_applicant) => Number(_applicant.userId) === Number(auth.id)
  ) as Applicant;

  const applicationSigned = application?.signDetails?.status === ESIGN_STATUS.SIGNED;

  // Fetch KYC button lives in the header now — optional, no checkbox gating it.
  // Shown any time the applicant hasn't gone through digilocker auth yet and
  // the application itself isn't already signed.
  const showFetchKycBtn =
    isCKYC &&
    isDateEligibleForKycPayment &&
    applicant?.digilocker?.status !== 'authorized' &&
    !applicationSigned;

  // CHANGED: no more digilockerOptedOut. If the applicant went through
  // Fetch KYC (status === 'authorized') but hasn't signed the KYC yet,
  // Sign KYC takes over from eSign. Otherwise eSign stays available.
  const showKycSignButton =
    isCKYC &&
    isDateEligibleForKycPayment &&
    applicant?.digilocker?.status === 'authorized' &&
    applicant?.digilocker?.kycSignStatus !== ESIGN_STATUS.SIGNED;

  const currentApplicantKycSigned = applicant?.digilocker?.kycSignStatus === ESIGN_STATUS.SIGNED;

  // CHANGED: was `!isCKYC || currentApplicantKycSigned || digilockerOptedOut`.
  // Now shows by default (digilocker never started == not 'authorized'),
  // or once KYC-sign is done — and hides while Sign KYC is pending.
  const showEsignButton =
    application &&
    (!isCKYC || currentApplicantKycSigned || applicant?.digilocker?.status !== 'authorized') &&
    ![ESIGN_STATUS.SIGNED, ESIGN_STATUS.EXPIRED].includes(application.signDetails.status);

  // unchanged
  const showMakePaymentBtn =
    isIndividualResidentApplicant(application?.applicants?.[0]?.status) &&
    !paymentStepComplete &&
    isCKYC &&
    isDateEligibleForKycPayment &&
    application?.signDetails?.status === ESIGN_STATUS.SIGNED;

  // unchanged — footer no longer includes showFetchKycBtn since that button moved to the header
  const showStickyFooter = showKycSignButton || showEsignButton || showMakePaymentBtn;

  // unchanged
  const handleEsignClick = () => {
    isIndividualResidentApplicant(application?.applicants?.[0]?.status) &&
    isOverseas &&
    !paymentCompleted &&
    isCKYC
      ? markPaymentOfflineAndProceed(handleEsign)
      : handleEsign();
  };

  return (
    <Box sx={{ bgcolor: { xs: '', sm: 'rgba(238, 244, 251, 0.5)' } }}>
      <IconButton
        sx={{
          position: 'fixed',
          right: 0,
          bottom: showStickyFooter ? 200 : 75,
          borderRadius: '5px 0 0 5px',
          '&,:hover': { bgcolor: 'primary.main' },
        }}
        aria-label="Back to Top"
        onClick={() => applicationDetailRef.current?.scrollIntoView({ behavior: 'smooth' })}>
        <ArrowUpwardIcon fontSize="large" sx={{ color: 'common.white' }} />
      </IconButton>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          bgcolor: 'common.white',
          display: 'flex',
          flexDirection: 'row',
          justifyContent: 'space-between',
          alignItems: 'center', // 👈 fixes the stretch
        }}>
        <Box sx={{ padding: '20px' }}>
          <CardMedia
            component="img"
            src="/images/edelweiss-logo.svg"
            alt="Logo"
            sx={{ width: 'unset', mx: 3, height: '40px' }}
          />
        </Box>
        <IconButton
          sx={{ mr: 5 }}
          onClick={() => {
            history.push(`/investment-details/${referenceId}/details`);
            dispatch(logout());
          }}>
          <ExitToApp fontSize="large" sx={{ color: 'primary.main' }} />
        </IconButton>
      </AppBar>
      <Divider sx={{ display: { xs: 'block', sm: 'none' } }} />
      <Box
        sx={{ py: 5, px: { xs: 5, sm: 10 }, pb: showStickyFooter ? '140px' : 5 }}
        ref={applicationDetailRef}>
        {' '}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            mb: 2,
            justifyContent: 'space-between',
            mt: 10,
          }}>
          <Typography
            component="h1"
            sx={{ fontSize: 20, fontWeight: 600, color: 'primary.main', mb: 4 }}>
            Application Details
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {showFetchKycBtn && (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: '8px',
                }}>
                <LoadingButton
                  onClick={triggerDigilocker}
                  loading={digiLockerLoading}
                  sx={{
                    bgcolor: 'primary.main',
                    color: 'common.white',
                    minWidth: '90px',
                    px: 1.5,
                    py: 0.5,
                    fontSize: 13,
                    fontWeight: 600,
                    lineHeight: 1.5,
                    '&:hover': { bgcolor: 'primary.dark' },
                    '&.Mui-disabled': {
                      bgcolor: 'action.disabledBackground',
                      color: 'action.disabled',
                    },
                  }}>
                  Update KYC
                </LoadingButton>
                <Tooltip title="Fetch and update the latest KYC details for this applicant via DigiLocker">
                  <InfoOutlinedIcon sx={{ fontSize: 18 }} />
                </Tooltip>
              </div>
            )}
            {application?.editable && (
              <IconButton disabled={isSubmitting}>
                <CardMedia
                  component="img"
                  src="/images/edit-icon-outlined.svg"
                  alt="Edit Icon"
                  sx={{ width: 'unset' }}
                  onClick={() =>
                    history.push(`/investment-details/edit-application`, {
                      id: application?.id,
                      applicant1ReferenceId: application?.applicant1ReferenceId,
                    })
                  }
                />
              </IconButton>
            )}
          </Box>
        </Box>
        {application && (
          <>
            <ApplicationDetailsCommonLayout
              loading={loading}
              application={application as ApplicationProps}
            />
            {showStickyFooter && (
              <Paper
                elevation={4}
                sx={{
                  position: 'fixed',
                  left: 0,
                  right: 0,
                  bottom: 0,
                  zIndex: (theme) => theme.zIndex.appBar,
                  width: '100%',
                  bgcolor: 'common.white',
                  p: 3,
                  borderRadius: 0,
                }}>
                <Box sx={{ textAlign: 'center' }}>
                  {/* {showFetchKycBtn && (
                    <Formik
                      initialValues={{ digiLockerConsent: false }}
                      onSubmit={() => {
                        // no-op: this Formik instance is only used for the checkbox's field state
                      }}>
                      {({ values }) => {
                        useEffect(() => {
                          setDigiLockerConsent(values.digiLockerConsent);
                        }, [values.digiLockerConsent]);
                        return (
                          <>
                            <MFCheckbox
                              name="digiLockerConsent"
                              label="Allow us to fetch your KYC details from DigiLocker"
                              // disabled={!isUSTech}
                              sx={{ mt: 1 }}
                            />
                            <Box>
                              {digiLockerConsent ? (
                                <LoadingButton
                                  onClick={triggerDigilocker}
                                  loading={digiLockerLoading}
                                  disabled={!digiLockerConsent}
                                  sx={{
                                    mt: 2,
                                    bgcolor: 'primary.main',
                                    color: 'common.white',
                                    minWidth: '200px',
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                    '&:hover': { bgcolor: 'primary.dark' },
                                    '&.Mui-disabled': {
                                      bgcolor: 'action.disabledBackground',
                                      color: 'action.disabled',
                                    },
                                  }}>
                                  Fetch KYC Details
                                </LoadingButton>
                              ) : (
                                <Button
                                  variant="contained"
                                  sx={{
                                    color: 'common.white',
                                    minWidth: '200px',
                                    mt: 2,
                                    fontWeight: 600,
                                    lineHeight: 1.5,
                                  }}
                                  onClick={handleEsignClick}
                                  disabled={isSubmitting || isSigningOpen}>
                                  {isSigningOpen
                                    ? 'Signing in progress...'
                                    : `e-Sign ${isSubmitting ? '...' : ''}`}
                                </Button>
                              )}
                            </Box>
                          </>
                        );
                      }}
                    </Formik>
                  )} */}

                  {showKycSignButton && (
                    <Button
                      variant="contained"
                      sx={{
                        color: 'common.white',
                        minWidth: '200px',
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                      onClick={() => handleKycEsign(referenceId, refetchApplication)}
                      disabled={isSubmitting}>
                      Sign KYC
                    </Button>
                  )}

                  {!showKycSignButton && showEsignButton && (
                    <>
                      <Button
                        variant="contained"
                        sx={{
                          color: 'common.white',
                          minWidth: '200px',
                          fontWeight: 600,
                          lineHeight: 1.5,
                        }}
                        onClick={handleEsignClick}
                        disabled={isSubmitting || isSigningOpen}>
                        {isSigningOpen
                          ? 'Signing in progress...'
                          : `e-Sign ${isSubmitting ? '...' : ''}`}
                      </Button>
                      {isSigningOpen && (
                        <Typography sx={{ mt: 1, fontSize: 13, color: 'text.secondary' }}>
                          Please complete the signing in the opened window
                        </Typography>
                      )}
                      <Typography
                        sx={{
                          mt: 2,
                          fontSize: 14,
                          fontWeight: 700,
                          color: 'text.primary',
                          lineHeight: 1.7,
                          textAlign: 'center',
                        }}>
                        ({ESIGN_MESSAGE})
                      </Typography>
                    </>
                  )}

                  {!showKycSignButton && !showEsignButton && showMakePaymentBtn && (
                    <Button
                      variant="contained"
                      sx={{
                        color: 'common.white',
                        minWidth: '200px',
                        fontWeight: 600,
                        lineHeight: 1.5,
                      }}
                      onClick={handleMakePayment}
                      disabled={isSubmitting || isPaymentSubmitting}>
                      Make Payment
                    </Button>
                  )}
                </Box>
              </Paper>
            )}
          </>
        )}
      </Box>
      <Footer />

      {/* {ConfirmDialog} */}

      <PaymentPopups
        showPaymentSuccessPopup={showPaymentSuccessPopup}
        onCloseSuccessPopup={() => setShowPaymentSuccessPopup(false)}
        onPaymentSuccessOk={async () => {
          setShowPaymentSuccessPopup(false);
          // await handleEsign();
        }}
        successOkDisabled={isSubmitting}
        showPaymentFailurePopup={showPaymentFailurePopup}
        onPaymentFailureOk={async () => {
          setShowPaymentFailurePopup(false);
          // await handleEsign();
        }}
        showPaymentModePopup={showPaymentModePopup}
        onClosePaymentModePopup={() => setShowPaymentModePopup(false)}
        onPaymentModeConfirm={handlePaymentModeConfirm}
        isOnlinePaymentEnabled={isOnlinePaymentEnabled}
        showOfflinePaymentEsignPopup={showOfflinePaymentEsignPopup}
        onCloseOfflinePopup={() => setShowOfflinePaymentEsignPopup(false)}
        onProceedWithEsign={async () => {
          setShowOfflinePaymentEsignPopup(false);
          // await handleEsign();
        }}
        proceedDisabled={isSubmitting}
      />
    </Box>
  );
}
